import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { CTableWrapper, SortedOrder } from '../../../dum';
import {
    DeltaMoney,
    DeltaPercent,
    formatMoney,
} from './expenses-summary-delta';

// One entity (resource category or supplier account) compared across the
// selected month and the one before it. Rows are produced by the page from the
// year-wide matrices; `avg3` is the mean of the three months preceding the
// selected one, which guards against reading a change as meaningful when it was
// last month that was the anomaly.
export type ComparisonRow = {
    id: number;
    name: string;
    previous: number;
    current: number;
    delta: number;
    // null → grew from zero (percentage would be infinite).
    deltaPct: number | null;
    avg3: number;
    // undefined → no trailing history to compare against.
    vsAvgPct: number | null | undefined;
};

// The value a column sorts on. `undefined` means "nothing to compare" (the em
// dash cells) and always sinks to the bottom, whichever direction is active —
// otherwise ascending order would open with a wall of blanks.
function sortValue(
    row: ComparisonRow,
    index: number,
): string | number | undefined {
    switch (index) {
        case 0:
            return row.name;
        case 1:
            return row.previous;
        case 2:
            return row.current;
        case 3:
            return row.delta;
        // "nuevo" (grew from zero) is conceptually an infinite increase, so it
        // sorts above every finite percentage.
        case 4:
            return row.deltaPct === null
                ? Number.POSITIVE_INFINITY
                : row.deltaPct;
        case 5:
            return row.vsAvgPct === undefined
                ? undefined
                : row.vsAvgPct === null
                ? Number.POSITIVE_INFINITY
                : row.vsAvgPct;
        default:
            return undefined;
    }
}

export function ExpensesSummaryComparisonTable({
    rows,
    loading,
    entityColumnTitle,
    previousMonthLabel,
    monthLabel,
    childRowsOf,
    childColumnTitle,
    stickyHeader = true,
}: {
    rows: ComparisonRow[];
    loading?: boolean;
    entityColumnTitle: string;
    previousMonthLabel: string;
    monthLabel: string;
    // Off for the nested drill-down table, which sits inside the outer table's
    // scroll area and would otherwise pin a second header over it.
    stickyHeader?: boolean;
    // When provided, every row becomes expandable into the same table one level
    // down (category → resource, supplier → resource). Called lazily by
    // CTableWrapper, so a closed row costs nothing.
    childRowsOf?: (row: ComparisonRow) => ComparisonRow[];
    childColumnTitle?: string;
}) {
    // Sorting lives here rather than in the page so each nested table sorts
    // independently of its parent.
    const [sortedIndex, setSortedIndex] = useState<number | null>(null);
    const [sortedOrder, setSortedOrder] = useState<SortedOrder>(null);

    const sortedRows = useMemo(() => {
        const copy = [...rows];
        // No explicit sort → "top movers": the biggest swing in either
        // direction first, which is the question this table exists to answer.
        if (sortedIndex === null || sortedOrder === null) {
            copy.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
            return copy;
        }
        const direction = sortedOrder === 'asc' ? 1 : -1;
        copy.sort((a, b) => {
            const aValue = sortValue(a, sortedIndex);
            const bValue = sortValue(b, sortedIndex);
            if (aValue === undefined && bValue === undefined) return 0;
            if (aValue === undefined) return 1;
            if (bValue === undefined) return -1;
            if (typeof aValue === 'string' || typeof bValue === 'string') {
                return (
                    direction *
                    String(aValue).localeCompare(String(bValue), 'es')
                );
            }
            return direction * (aValue - bValue);
        });
        return copy;
    }, [rows, sortedIndex, sortedOrder]);

    // Totals are summed from the same rows the table renders, so the footer
    // always agrees with what is on screen.
    const totals = rows.reduce(
        (acc, row) => {
            acc.previous += row.previous;
            acc.current += row.current;
            acc.avg3 += row.avg3;
            return acc;
        },
        { previous: 0, current: 0, avg3: 0 },
    );
    const totalDelta = totals.current - totals.previous;
    const totalDeltaPct = totals.previous
        ? (totalDelta / totals.previous) * 100
        : totals.current
        ? null
        : 0;
    const totalVsAvgPct = totals.avg3
        ? ((totals.current - totals.avg3) / totals.avg3) * 100
        : undefined;

    return (
        <CTableWrapper
            stickyHeader={stickyHeader}
            loading={loading}
            sortedIndex={sortedIndex}
            setSortedIndex={setSortedIndex}
            sortedOrder={sortedOrder}
            setSortedOrder={setSortedOrder}
            items={[
                {
                    // Text column → left-aligned (house rule); every numeric
                    // column below is right-aligned.
                    title: entityColumnTitle,
                    content: (row) => row.name,
                    sortable: true,
                    cellGrow: 2,
                },
                {
                    title: previousMonthLabel,
                    textAlign: 'right',
                    content: (row) => formatMoney(row.previous),
                    sortable: true,
                },
                {
                    title: monthLabel,
                    textAlign: 'right',
                    content: (row) => formatMoney(row.current),
                    sortable: true,
                },
                {
                    title: 'Cambio',
                    textAlign: 'right',
                    content: (row) => <DeltaMoney value={row.delta} />,
                    sortable: true,
                },
                {
                    title: '%',
                    textAlign: 'right',
                    content: (row) => <DeltaPercent value={row.deltaPct} />,
                    sortable: true,
                },
                {
                    title: 'vs prom. 3 meses',
                    textAlign: 'right',
                    content: (row) => <DeltaPercent value={row.vsAvgPct} />,
                    sortable: true,
                },
            ]}
            data={sortedRows}
            collapse={
                childRowsOf
                    ? (row) => {
                          const children = childRowsOf(row);
                          if (children.length === 0) {
                              return (
                                  <Typography
                                      variant={'body2'}
                                      sx={{ color: 'text.secondary' }}
                                  >
                                      Sin desglose disponible.
                                  </Typography>
                              );
                          }
                          return (
                              <Box>
                                  <ExpensesSummaryComparisonTable
                                      rows={children}
                                      entityColumnTitle={
                                          childColumnTitle ?? 'Recurso'
                                      }
                                      previousMonthLabel={previousMonthLabel}
                                      monthLabel={monthLabel}
                                      stickyHeader={false}
                                  />
                              </Box>
                          );
                      }
                    : undefined
            }
            totalsRow={[
                'Total',
                formatMoney(totals.previous),
                formatMoney(totals.current),
                <DeltaMoney value={totalDelta} />,
                <DeltaPercent value={totalDeltaPct} />,
                <DeltaPercent value={totalVsAvgPct} />,
            ]}
        />
    );
}
